import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Palette } from 'lucide-react';
import { apiRequest } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ArtistPage() {
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArtists();
  }, []);

  const fetchArtists = async () => {
    try {
      const response = await apiRequest(`${BACKEND_URL}/api/admin/artists/public/list`, {
        method: 'GET',
      });

      if (response.ok) {
        setArtists(response.data.artists || []);
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch artists:', err);
      setLoading(false);
    }
  };

  // Count cards per artist
  const getArtistCardCount = (artistId) => {
    // This would ideally come from the backend
    // For now, return a placeholder
    return Math.floor(Math.random() * 30) + 10;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl lg:text-6xl font-extrabold text-navy mb-6">
            Meet Our Artists
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover talented artists creating unique, hand-painted business cards. Each design is a one-of-a-kind masterpiece.
          </p>
        </div>
      </section>

      {/* Artists Grid */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading artists...</p>
            </div>
          ) : artists.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl shadow-sm">
              <Palette className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No artists available yet</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {artists.map((artist) => (
                <div key={artist.id} className="bg-white rounded-[2rem] overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                  {/* Banner or Gradient Background */}
                  <div 
                    className="p-8 flex justify-center items-center relative"
                    style={{
                      backgroundImage: artist.banner_picture 
                        ? `url(${BACKEND_URL}${artist.banner_picture})`
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      minHeight: '200px'
                    }}
                  >
                    {/* Profile Picture */}
                    {artist.profile_picture ? (
                      <img
                        src={`${BACKEND_URL}${artist.profile_picture}`}
                        alt={artist.name}
                        className="w-32 h-32 rounded-full bg-white object-cover border-4 border-white shadow-lg"
                      />
                    ) : (
                      <div className="w-32 h-32 rounded-full bg-white flex items-center justify-center text-purple-600 font-bold text-5xl border-4 border-white shadow-lg">
                        {artist.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-navy mb-2">{artist.name}</h3>
                    
                    {artist.short_intro && (
                      <p className="text-gray-600 mb-4 text-sm leading-relaxed line-clamp-3">
                        {artist.short_intro}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-gray-600">
                        📧 {artist.email}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-sm text-gray-600">
                        📞 {artist.phone}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-4 pt-2 border-t border-gray-200">
                      <Palette className="w-5 h-5 text-navy" />
                      <span className="text-sm font-semibold text-navy">
                        Professional Artist
                      </span>
                    </div>
                    
                    <Button 
                      onClick={() => navigate(`/artist/${artist.id}`)}
                      className="w-full bg-navy text-white hover:bg-navy/90 rounded-full py-3"
                    >
                      View Profile
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
